import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import { useMedia } from 'react-use'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Image from '@/components/base/lazy-image'
import Button from '@/components/base/play-button'
import Section from '@/components/base/section'
import { useAppStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type VideoPlayerProps = HTMLAttributes<HTMLDivElement> & Partial<{ media: WPMedia }>

const VideoPlayer = ({ media, ...props }: VideoPlayerProps) => {
  const { strings } = useAppStore()
  const [isPlaying, setIsPlaying] = useState(false)
  const video = useRef<HTMLVideoElement>(null!)
  const isSM = useMedia('(max-width: 767px)', true)
  const imageSource = isSM && media?.image_mobile?.url ? media?.image_mobile : media?.image
  const videoSource = isSM && media?.video_mobile?.url ? media?.video_mobile : media?.video

  const playVideo = useCallback(() => {
    video.current?.play()
  }, [])

  useEffect(() => {
    const currentVideo = video.current

    const handleVideoPlay = () => {
      setIsPlaying(true)
    }

    const handleVideoEnded = () => {
      setIsPlaying(false)
    }

    currentVideo?.addEventListener('play', handleVideoPlay)
    currentVideo?.addEventListener('ended', handleVideoEnded)

    return () => {
      currentVideo?.removeEventListener('play', handleVideoPlay)
      currentVideo?.removeEventListener('ended', handleVideoEnded)
    }
  }, [])

  return (
    <Section {...props}>
      <Container>
        <Grid className="text-white">
          <div className="relative col-span-2 aspect-video md:col-span-6 lg:col-span-12">
            <video
              ref={video}
              controls
              playsInline
              poster={imageSource?.url}
              className="absolute left-0 top-0 size-full object-contain"
            >
              <source src={videoSource?.url} type="video/mp4" />
            </video>

            <div
              className={cn(
                'absolute left-0 top-0 flex size-full items-center justify-center',
                'after:absolute after:left-0 after:top-0 after:size-full after:bg-dark/50',
                'transition-all duration-500 ease-out-cubic',
                {
                  'invisible opacity-0': isPlaying
                }
              )}
            >
              <Image
                src={imageSource?.url || ''}
                alt={imageSource?.alt || ''}
                className="object-contain"
                fill
              />

              <Button className="dark relative z-10" onClick={playVideo}>
                {strings?.labels?.play}
              </Button>
            </div>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default VideoPlayer
